// src/pages/professional/Schedule.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { appointmentsService, rescheduleService, notificationsService, scheduleWindowsService, usersService } from 'infrastructure/firebase';
import { StatusBadge, Avatar, Button, EmptyState, Modal, FormGroup, Input, Select, Alert } from 'shared/components/ui';
import { formatDate, formatCurrency, monthName, getDaysInMonth, getFirstDayOfMonth } from 'shared/utils/helpers';
import { useMobile } from 'shared/hooks/useMobile';
import { getDaySummary, validateWindowConflicts, slotsInRange } from 'shared/utils/scheduleLogic';

const DAYS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const TIME_OPTIONS = slotsInRange('07:00', '20:00').concat(['20:00']);

const STATUS_WIN = {
  pending:  { bg:'#FEF9C3', border:'#FCD34D', color:'#92400E', label:'Aguardando aprovação' },
  approved: { bg:'#E7F4ED', border:'#8FC2A6', color:'#2D5F43', label:'Aprovado — bloqueado' },
  rejected: { bg:'#FEE2E2', border:'#FCA5A5', color:'#991B1B', label:'Recusado' },
};

// ── Window request form ────────────────────────────────────────
function WindowRequestPanel({ professionalId, professionalName, appointments, windows, onClose, showToast, notifyAdmin }) {
  const [form, setForm] = useState({ date:'', startTime:'08:00', endTime:'12:00', reason:'' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const conflicts = useMemo(() => {
    if (!form.date || !form.startTime || !form.endTime) return [];
    return validateWindowConflicts({
      date: form.date, startTime: form.startTime, endTime: form.endTime,
      professionalId, allProfessionals: false,
      appointments, windows,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, form.startTime, form.endTime, appointments, windows, professionalId]);

  const canSend = form.date && form.startTime && form.endTime && form.startTime < form.endTime;

  const handleSend = async () => {
    setSaving(true);
    try {
      await scheduleWindowsService.add({
        professionalId, professionalName,
        allProfessionals: false,
        date:      form.date,
        startTime: form.startTime,
        endTime:   form.endTime,
        reason:    form.reason,
        status:    'pending',
        createdBy: professionalId,
        createdByRole: 'professional',
        approvedBy: null,
        approvedAt: null,
      });
      await notifyAdmin(`${professionalName} solicitou bloqueio de agenda em ${formatDate(form.date)} (${form.startTime}–${form.endTime})${form.reason ? ': ' + form.reason : ''}.`);
      showToast('Solicitação enviada ao administrador para aprovação!');
      onClose();
    } catch {
      showToast('Erro ao enviar solicitação.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)', backdropFilter:'blur(6px)', zIndex:150 }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'min(440px,100vw)', background:'#fff', zIndex:151, boxShadow:'-12px 0 40px rgba(74,63,88,0.18)', display:'flex', flexDirection:'column', fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ padding:'22px 28px', borderBottom:'1px solid #E8E5EC', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:22, color:'#3D3447' }}>Solicitar bloqueio de agenda</div>
            <div style={{ fontSize:12, color:'#A8A1B0', marginTop:2 }}>O administrador precisará aprovar antes de bloquear os slots</div>
          </div>
          <button onClick={onClose} style={{ background:'#F0EDF3', border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, color:'#6B6070', fontSize:18 }}>×</button>
        </div>

        <div style={{ padding:'24px 28px', flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:18 }}>
          <div style={{ background:'#FEF9C3', border:'1px solid #FCD34D', borderRadius:12, padding:'12px 14px', fontSize:13, color:'#92400E' }}>
            ⏳ Sua solicitação ficará como <strong>pendente</strong> até o admin aprovar. Os slots só são bloqueados após aprovação.
          </div>

          <div>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>Data <span style={{ color:'#C9929A' }}>*</span></label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} min={new Date().toISOString().split('T')[0]}
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', color:'#3D3447', background:'#fff', boxSizing:'border-box' }} />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>Início <span style={{ color:'#C9929A' }}>*</span></label>
              <select value={form.startTime} onChange={e => set('startTime', e.target.value)}
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', color:'#3D3447', background:'#fff' }}>
                {TIME_OPTIONS.slice(0,-1).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>Fim <span style={{ color:'#C9929A' }}>*</span></label>
              <select value={form.endTime} onChange={e => set('endTime', e.target.value)}
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', color:'#3D3447', background:'#fff' }}>
                {TIME_OPTIONS.slice(1).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {form.startTime >= form.endTime && (
            <div style={{ fontSize:12, color:'#7A3C44', padding:'8px 12px', background:'#F5E6E8', borderRadius:8 }}>⚠️ Horário de fim deve ser após o início.</div>
          )}

          <div>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>Motivo (opcional)</label>
            <input value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Ex.: Consulta médica, Curso, Férias..."
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', color:'#3D3447', background:'#fff', boxSizing:'border-box' }} />
          </div>

          {conflicts.length > 0 && (
            <div style={{ background:'#F5E4D2', border:'1px solid #DDB48E', borderRadius:12, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:13, color:'#80551F', marginBottom:6 }}>⚠️ {conflicts.length} conflito(s) encontrado(s)</div>
              {conflicts.map((c, i) => <div key={i} style={{ fontSize:12, color:'#80551F', marginTop:4 }}>• {c.message}</div>)}
            </div>
          )}
        </div>

        <div style={{ padding:'16px 28px', borderTop:'1px solid #E8E5EC', background:'#FAF7FB', display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!canSend || saving} loading={saving} onClick={handleSend}>Enviar solicitação</Button>
        </div>
      </div>
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────
export default function ProfessionalSchedule() {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { appointments, scheduleWindows, showToast } = useApp();

  const now = new Date();
  const [calYear, setCalYear]   = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0]);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [cancelModal, setCancelModal]         = useState(null);
  const [windowPanel, setWindowPanel]         = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const myId    = userProfile?.id;
  const myAppts = appointments.filter(a => a.professionalId === myId);

  // Janelas que afetam este profissional
  const myWindows = scheduleWindows.filter(w =>
    w.professionalId === myId || w.allProfessionals
  );

  // Resumo do dia selecionado (agendamentos + bloqueios juntos)
  const daySummary = useMemo(() =>
    getDaySummary({ date: selectedDate, professionalId: myId, appointments, windows: myWindows }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [selectedDate, myId, appointments, myWindows]);

  // Para cada dia no calendário: tem agendamentos? tem bloqueios?
  const apptsByDate = useMemo(() => {
    const map = {};
    myAppts.forEach(a => { if (!map[a.date]) map[a.date] = []; map[a.date].push(a); });
    return map;
  }, [myAppts]);

  const winsByDate = useMemo(() => {
    const map = {};
    myWindows.filter(w => w.status !== 'rejected').forEach(w => {
      if (!map[w.date]) map[w.date] = [];
      map[w.date].push(w);
    });
    return map;
  }, [myWindows]);

  // Calendar
  const firstDay = getFirstDayOfMonth(calYear, calMonth);
  const daysInM  = getDaysInMonth(calYear, calMonth);
  const cells    = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInM; d++) cells.push(d);

  const handleReschedule = async () => {
    if (!newDate || !newTime || !rescheduleModal) return;
    setLoading(true);
    try {
      await rescheduleService.request({
        appointmentId: rescheduleModal.id, clientId: rescheduleModal.clientId,
        clientName: rescheduleModal.clientName, professionalName: userProfile?.name,
        originalDate: rescheduleModal.date, originalTime: rescheduleModal.time,
        newDate, newTime, message,
      });
      await notificationsService.add({
        userId: rescheduleModal.clientId, type: 'reschedule',
        title: 'Solicitação de troca de horário',
        message: `${userProfile?.name} solicitou trocar seu atendimento de ${formatDate(rescheduleModal.date)} ${rescheduleModal.time} para ${formatDate(newDate)} ${newTime}. ${message ? 'Mensagem: ' + message : ''}`,
        rescheduleData: { appointmentId: rescheduleModal.id, newDate, newTime },
      });
      showToast('Solicitação de troca enviada ao cliente!');
      setRescheduleModal(null); setNewDate(''); setNewTime(''); setMessage('');
    } catch { showToast('Erro ao enviar solicitação.', 'error'); }
    finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setLoading(true);
    try {
      await appointmentsService.cancel(cancelModal.id);
      showToast('Agendamento cancelado.');
      setCancelModal(null);
    } catch { showToast('Erro ao cancelar.', 'error'); }
    finally { setLoading(false); }
  };

  const handleMarkDone = async (appt) => {
    try {
      await appointmentsService.markDone(appt.id);
      showToast(`Atendimento de ${appt.clientName} marcado como realizado!`);
    } catch { showToast('Erro ao marcar atendimento.', 'danger'); }
  };

  const notifyAdmin = async (msg) => {
    try {
      // Busca todos os admins para notificar (sem hardcode)
      const admins = await usersService.getByRole('admin');
      await Promise.all(admins.map(admin =>
        notificationsService.add({
          userId: admin.id, type: 'window_request',
          title: 'Nova solicitação de bloqueio de agenda', message: msg,
        })
      ));
    } catch (err) {
      console.error('Erro ao notificar admins:', err);
    }
  };

  const prevMonth = () => { if (calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); };

  // Janelas pendentes minhas
  const myPendingWindows = myWindows.filter(w => w.status === 'pending' && w.professionalId === myId);

  return (
    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap:24 }}>
      {/* Left: day timeline */}
      <div>
        {/* Pending alerts */}
        {myPendingWindows.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <Alert type="warning" title={`${myPendingWindows.length} solicitação(ões) de bloqueio aguardando aprovação`}>
              {myPendingWindows.map(w => (
                <div key={w.id} style={{ fontSize:13, color:'#80551F', marginTop:6 }}>
                  • {formatDate(w.date)} · {w.startTime}–{w.endTime} {w.reason ? `(${w.reason})` : ''}
                </div>
              ))}
            </Alert>
          </div>
        )}

        <div className="card" style={{ marginBottom:0 }}>
          <div className="card-header">
            <div>
              <div className="card-title">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long' })}
              </div>
              <div className="card-subtitle">
                {daySummary.filter(e => e.type === 'appointment').length} atendimento(s) ·{' '}
                {daySummary.filter(e => e.type === 'window').length} bloqueio(s)
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setWindowPanel(true)}>+ Solicitar bloqueio</Button>
          </div>
          <div>
            {daySummary.length === 0 ? (
              <EmptyState icon={selectedDate < now.toISOString().split('T')[0] ? '📁' : '🌟'}
                title={selectedDate < now.toISOString().split('T')[0] ? 'Sem registros neste dia' : 'Dia livre!'}
                text="Clique em outro dia no calendário para ver a agenda." />
            ) : daySummary.map((entry) => {
              if (entry.type === 'window') {
                const si = STATUS_WIN[entry.status] || STATUS_WIN.pending;
                return (
                  <div key={entry.id} style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', gap:14, background:si.bg }}>
                    <div style={{ width:56, flexShrink:0, textAlign:'center' }}>
                      <div style={{ fontWeight:700, color:si.color, fontSize:16 }}>{entry.time}</div>
                      <div style={{ fontSize:10, color:si.color }}>até {entry.endTime}</div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13, color:si.color }}>🚫 {entry.label}</div>
                      <div style={{ fontSize:11, color:si.color, marginTop:2, opacity:.8 }}>{si.label}</div>
                    </div>
                  </div>
                );
              }
              // appointment
              const a = entry.raw;
              return (
                <div key={a.id} style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', gap:14 }}>
                  <div style={{ width:56, flexShrink:0, textAlign:'center' }}>
                    <div style={{ fontWeight:700, color:'var(--accent)', fontSize:18 }}>{a.time}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)'}}>{a.duration}min</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <Avatar name={a.clientName||'?'} size="sm" />
                        <div>
                          <div style={{ fontWeight:600, fontSize:14 }}>{a.clientName}</div>
                          <div style={{ fontSize:12, color:'var(--text-muted)' }}>💆 {a.procedureName} · {formatCurrency(a.price)}</div>
                        </div>
                      </div>
                      <StatusBadge status={a.status} />
                    </div>
                    {a.status !== 'cancelled' && (
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        {!a.done && a.status === 'confirmed' && (
                          <Button variant="success" size="sm" onClick={() => handleMarkDone(a)}>✓ Marcar Realizado</Button>
                        )}
                        {a.done && <span className="badge badge-success">✓ Realizado</span>}
                        {!a.done && <Button variant="secondary" size="sm" onClick={() => setRescheduleModal(a)}>🔄 Trocar Horário</Button>}
                        {!a.done && (
                          <Button variant="secondary" size="sm" onClick={() => navigate(`/pro/chat/${a.id}`)}>💬 Chat</Button>
                        )}
                        {!a.done && <Button variant="danger"    size="sm" onClick={() => setCancelModal(a)}>✗ Cancelar</Button>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: mini calendar */}
      <div>
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          <div style={{ background:'var(--brand)', color:'#fff', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <button onClick={prevMonth} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', width:30, height:30, borderRadius:6, cursor:'pointer', fontSize:16 }}>‹</button>
            <span style={{ fontFamily:"'Instrument Serif',serif", fontSize:17 }}>{monthName(calMonth)} {calYear}</span>
            <button onClick={nextMonth} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', width:30, height:30, borderRadius:6, cursor:'pointer', fontSize:16 }}>›</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:'var(--surface-2)', borderBottom:'1px solid var(--border)' }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign:'center', padding:'8px 2px', fontSize:9, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'var(--text-muted)' }}>{d}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} style={{ height:44, borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background:'var(--surface-2)' }} />;
              const iso = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const todayISO = now.toISOString().split('T')[0];
              const hasAppts = (apptsByDate[iso]||[]).length > 0;
              const hasWin   = (winsByDate[iso]||[]).some(w => w.status === 'approved');
              const hasPendingWin = (winsByDate[iso]||[]).some(w => w.status === 'pending');
              const isToday   = iso === todayISO;
              const isSelected = iso === selectedDate;
              return (
                <div key={iso} onClick={() => setSelectedDate(iso)}
                  style={{
                    height:44, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column',
                    borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)',
                    cursor:'pointer', position:'relative',
                    background: isSelected ? 'var(--accent)' : isToday ? '#fce7eb' : hasWin ? '#F0EDF3' : 'var(--surface)',
                  }}>
                  <span style={{ fontSize:13, fontWeight:500, color: isSelected ? '#fff' : isToday ? 'var(--accent)' : 'var(--text-secondary)' }}>{day}</span>
                  <div style={{ display:'flex', gap:2, marginTop:1 }}>
                    {hasAppts && !isSelected && <div style={{ width:4, height:4, borderRadius:'50%', background: isToday?'var(--accent)':'var(--success)' }} />}
                    {hasWin && !isSelected && <div style={{ width:4, height:4, borderRadius:'50%', background:'#6B5876' }} />}
                    {hasPendingWin && !isSelected && <div style={{ width:4, height:4, borderRadius:'50%', background:'#FCD34D' }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legenda */}
        <div style={{ marginTop:12, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14 }}>
          <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'var(--text-muted)', marginBottom:8 }}>Legenda</div>
          {[
            { dot:'var(--success)', label:'Agendamentos' },
            { dot:'#6B5876',        label:'Bloqueio aprovado' },
            { dot:'#FCD34D',        label:'Aguardando aprovação' },
          ].map(l => (
            <div key={l.label} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text-muted)', marginBottom:4 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:l.dot, flexShrink:0 }} />
              {l.label}
            </div>
          ))}
        </div>

        {/* Resumo do mês */}
        <div style={{ marginTop:12, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14 }}>
          <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'var(--text-muted)', marginBottom:8 }}>Resumo do mês</div>
          {[
            { label:'Total de agendamentos', value: myAppts.filter(a=>a.date?.startsWith(`${calYear}-${String(calMonth+1).padStart(2,'0')}`)).length },
            { label:'Realizados',            value: myAppts.filter(a=>a.done && a.date?.startsWith(`${calYear}-${String(calMonth+1).padStart(2,'0')}`)).length },
            { label:'Bloqueios aprovados',   value: myWindows.filter(w=>w.status==='approved' && w.date?.startsWith(`${calYear}-${String(calMonth+1).padStart(2,'0')}`)).length },
            { label:'Receita',               value: formatCurrency(myAppts.filter(a=>a.done && a.date?.startsWith(`${calYear}-${String(calMonth+1).padStart(2,'0')}`)).reduce((s,a)=>s+Number(a.price||0),0)) },
          ].map(item => (
            <div key={item.label} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
              <span style={{ color:'var(--text-muted)' }}>{item.label}</span>
              <span style={{ fontWeight:600 }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Window request panel */}
      {windowPanel && (
        <WindowRequestPanel
          professionalId={myId}
          professionalName={userProfile?.name}
          appointments={appointments}
          windows={scheduleWindows}
          showToast={showToast}
          notifyAdmin={notifyAdmin}
          onClose={() => setWindowPanel(false)}
        />
      )}

      {/* Reschedule Modal */}
      <Modal open={!!rescheduleModal} onClose={() => setRescheduleModal(null)} title="Solicitar Troca de Horário"
        footer={<>
          <Button variant="secondary" onClick={() => setRescheduleModal(null)}>Cancelar</Button>
          <Button variant="primary" loading={loading} disabled={!newDate||!newTime} onClick={handleReschedule}>Enviar Solicitação</Button>
        </>}>
        {rescheduleModal && (
          <>
            <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:14, marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Agendamento atual</div>
              <div style={{ fontSize:13, color:'var(--text-muted)' }}>
                {rescheduleModal.clientName} · {rescheduleModal.procedureName}<br/>
                {formatDate(rescheduleModal.date)} às {rescheduleModal.time}
              </div>
            </div>
            <FormGroup label="Nova data" required>
              <Input type="date" value={newDate} onChange={e=>setNewDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
            </FormGroup>
            <FormGroup label="Novo horário" required>
              <Select value={newTime} onChange={e=>setNewTime(e.target.value)}>
                <option value="">Selecione</option>
                {TIME_OPTIONS.slice(0,-1).map(t => <option key={t}>{t}</option>)}
              </Select>
            </FormGroup>
            <FormGroup label="Mensagem ao cliente (opcional)">
              <textarea className="form-input" rows={3} value={message} onChange={e=>setMessage(e.target.value)} placeholder="Explique o motivo da troca..." />
            </FormGroup>
          </>
        )}
      </Modal>

      {/* Cancel Modal */}
      <Modal open={!!cancelModal} onClose={() => setCancelModal(null)} title="Cancelar Agendamento"
        footer={<>
          <Button variant="secondary" onClick={() => setCancelModal(null)}>Não, manter</Button>
          <Button variant="danger" loading={loading} onClick={handleCancel}>Sim, cancelar</Button>
        </>}>
        {cancelModal && (
          <div>
            <p style={{ marginBottom:16 }}>Tem certeza que deseja cancelar este agendamento?</p>
            <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:14 }}>
              <div style={{ fontWeight:600 }}>{cancelModal.clientName}</div>
              <div style={{ color:'var(--text-muted)', fontSize:13 }}>
                {cancelModal.procedureName} · {formatDate(cancelModal.date)} às {cancelModal.time}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
